import { createFileRoute } from '@tanstack/react-router';
import { Construction, Settings2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { CopyableId } from '@/components/primitives/CopyableId';
import { EntityChip } from '@/components/primitives/EntityChip';
import { StateChip } from '@/components/primitives/StateChip';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { StateBlock } from '@/components/states/StateBlock';
import { useLeagueDetail, type LeagueDetail } from '@/features/leagues/leagues';

export const Route = createFileRoute('/leagues/$leagueId/')({
  component: OverviewTab,
});

function OverviewTab() {
  const { leagueId } = Route.useParams();
  const { data, isPending, isError, error, refetch } = useLeagueDetail(leagueId);

  if (isError) {
    return <ErrorState error={error} subject="this league" onRetry={() => void refetch()} />;
  }
  if (isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton height={160} />
        <Skeleton height={120} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Panel title="Settings" icon={Settings2}>
        <dl className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Name" value={data.name} />
          <Field label="Sport" value={data.sport} capitalize />
          <Field label="Format" value={data.leagueType} capitalize />
          <Field
            label="Size"
            value={`${data.filledTeams} of ${data.leagueSize ?? '?'} teams filled`}
            mono
          />
          <Field label="Members" value={`${data.memberCount} users`} mono />
          <Field
            label="Squad size"
            value={
              data.minSquadSize || data.maxSquadSize
                ? `${data.minSquadSize ?? '?'} – ${data.maxSquadSize ?? '?'}`
                : null
            }
            mono
          />
          <Field
            label="Visibility"
            value={data.isPublic === null ? null : data.isPublic ? 'Public' : 'Private'}
          />
          <Field label="Time zone" value={data.timeZone} mono />
          <Field
            label="Location"
            value={
              data.location
                ? [data.location.city, data.location.state].filter(Boolean).join(', ') || null
                : null
            }
          />
          <Field
            label="Association"
            value={
              data.association?.type
                ? `${data.association.type}${data.association.name ? ` · ${data.association.name}` : ''}`
                : null
            }
            capitalize
          />
          <Field
            label="Season config"
            value={
              data.seasonConfig
                ? `${data.seasonConfig.regularWeeks ?? '?'} regular + ${data.seasonConfig.playoffWeeks ?? '?'} playoff`
                : null
            }
            mono
          />
          <Field label="Created" value={data.createdAt?.slice(0, 10) ?? null} mono />
        </dl>

        {data.description && (
          <div className="mt-3">
            <FieldLabel>Description</FieldLabel>
            <p className="text-tx2 mt-0.5 text-[13px]">{data.description}</p>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <CopyableId value={data.id} label="League ID" truncate />
          {data.shareCode && <CopyableId value={data.shareCode} label="Invite code" />}
          {data.isSimulationLeague && (
            <StateChip state="simulation" size="sm" tone="live" label="Simulation league" />
          )}
        </div>
      </Panel>

      <Panel title="People">
        <dl className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
          <div>
            <FieldLabel>Commissioner</FieldLabel>
            <dd className="mt-1">
              {data.commissioner ? (
                <EntityChip
                  kind="user"
                  id={data.commissioner.id}
                  name={data.commissioner.name}
                  secondary={
                    data.commissioner.username ? `@${data.commissioner.username}` : undefined
                  }
                  to={`/users/${data.commissioner.id}`}
                />
              ) : (
                // A league with no resolvable commissioner is a real problem, not a
                // cosmetic gap — nobody can run it from inside the app.
                <span style={{ color: 'var(--warn)' }} className="text-[12.5px]">
                  No commissioner on record
                </span>
              )}
            </dd>
          </div>
          <Field
            label="Season"
            value={
              data.season
                ? `${String(data.season.year ?? '?')} · ${data.season.status}`
                : 'No season'
            }
          />
        </dl>
      </Panel>

      <SeasonTimeline data={data} />

      <Panel title="League actions">
        <StateBlock
          icon={Construction}
          tone="info"
          title="Blocked upstream, not unbuilt"
          description="Editing settings, regenerating the invite code, archiving and deleting all need endpoints that do not exist yet, and the delegated ones would be rejected by ZazzBackend until the canManageAsAdmin guard ships (OPEN_QUESTIONS item 2). Nothing is offered here rather than controls that would refuse."
        />
      </Panel>
    </div>
  );
}

/** Compact week strip — the fastest read of "where is this season". */
function SeasonTimeline({ data }: { data: LeagueDetail }) {
  if (data.weeks.length === 0) return null;

  return (
    <Panel title="Weeks" count={data.weeks.length}>
      <ol className="flex flex-wrap gap-1.5">
        {data.weeks.map((week, index) => (
          // Index-keyed because `week` can be null on a malformed row, and the list is
          // append-only in practice — it is never reordered or filtered.
          <li key={week.week ?? `unknown-${String(index)}`}>
            <span
              className="mesh-mono flex items-center gap-1 rounded border px-1.5 py-1 text-[11px]"
              style={{
                borderColor:
                  week.status === 'processing'
                    ? 'rgba(var(--warn-rgb), 0.5)'
                    : week.week === data.currentWeek?.week
                      ? 'var(--bd-s)'
                      : 'var(--bd)',
                background:
                  week.week === data.currentWeek?.week ? 'var(--high)' : 'transparent',
                color:
                  week.status === 'processing'
                    ? 'var(--warn)'
                    : week.status === 'complete'
                      ? 'var(--tx3)'
                      : 'var(--tx2)',
              }}
              title={`${week.status ?? 'unknown'}${week.lockAt ? ` · locks ${week.lockAt.slice(0, 16)}Z` : ''}`}
            >
              W{week.week ?? '?'}
            </span>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <dt
      className="font-head text-tx3 text-[10px] font-semibold uppercase"
      style={{ letterSpacing: '0.14em' }}
    >
      {children}
    </dt>
  );
}

function Field({
  label,
  value,
  mono = false,
  capitalize = false,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  capitalize?: boolean;
}) {
  return (
    <div className="min-w-0">
      <FieldLabel>{label}</FieldLabel>
      <dd
        className={[
          'mt-0.5 truncate',
          mono ? 'mesh-mono text-[12.5px]' : 'text-[13px]',
          capitalize ? 'capitalize' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ color: value ? 'var(--tx)' : 'var(--tx3)' }}
      >
        {value ?? 'not set'}
      </dd>
    </div>
  );
}

function Panel({
  title,
  icon: Icon,
  count,
  children,
}: {
  title: string;
  icon?: typeof Settings2;
  count?: number;
  children: ReactNode;
}) {
  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <h2
        className="font-head text-tx2 mb-2.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
        style={{ letterSpacing: '0.16em' }}
      >
        {Icon && <Icon size={12} aria-hidden />}
        {title}
        {count !== undefined && <span className="mesh-mono text-tx3 text-[11px]">{count}</span>}
      </h2>
      {children}
    </section>
  );
}
